import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { ToastContext } from "context/ToastContext";
import { useOptionalUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IDraftRunSummary,
  useDeleteContractModelDraftRunMutation,
  useListContractModelDraftRunsQuery,
} from "store/queries/contractModelDrafts";

import {
  CLEAR_FILTERS,
  CONTRACT_MODELS_EMPTY,
  CONTRACT_MODELS_TAB,
  CREATED_BY_COLUMN,
  DELETE_MODEL,
  DELETE_MODEL_FAILED,
  DELETE_MODEL_HEADER,
  DELETE_MODEL_MESSAGE,
  HOSPITAL_COLUMN,
  MODEL_DELETED,
  NEW_CONTRACT_MODEL,
  NO_MATCHING_CONTRACT_MODELS,
  NO_VALUE,
  runListTruncated,
} from "./copy";
import { NewContractModelDialog } from "./NewContractModelDialog";
import {
  DEFAULT_RUN_LIST_FILTERS,
  IRunListFilters,
  creatorLabel,
  creatorOptions,
  effectiveRunListFilters,
  filterRuns,
  hospitalOptions,
  pendingRunFilterOptions,
  readRunListFilters,
  writeRunListFilters,
} from "./runListFilters";
import { StandingInstructionsTab } from "./StandingInstructionsTab";
import {
  canDeleteRun,
  formatRunDate,
  runDisplayName,
  runStatusLabel,
  runStatusSeverity,
} from "./utils";

// Module-level table cell renderers (not defined during render).
const nameBody = (r: IDraftRunSummary) => (
  <span className="font-medium">{runDisplayName(r)}</span>
);
const statusBody = (r: IDraftRunSummary) => (
  <Tag
    value={runStatusLabel(r.status)}
    severity={runStatusSeverity(r.status)}
  />
);
const createdBody = (r: IDraftRunSummary) => formatRunDate(r.createdAt);
const hospitalBody = (r: IDraftRunSummary) => r.accountName || NO_VALUE;
/** One stable empty list, so the memos below do not recompute on every render before the first load. */
const NO_RUNS: IDraftRunSummary[] = [];

/** The API's message on a refusal (e.g. the 409 naming which delete condition failed). */
const errorDetail = (e: unknown): string => {
  const apiMessage = (e as { data?: { message?: string } })?.data?.message;
  return typeof apiMessage === "string" && apiMessage.length > 0
    ? apiMessage
    : "Something went wrong. Please try again.";
};

export function ContractModelDrafts() {
  const navigate = useNavigate();

  const toastRef = useContext(ToastContext);
  // Canceled, failed and dead runs are hidden by the server unless asked for.
  const [showInactive, setShowInactive] = useState(false);
  const { data: runList } = useListContractModelDraftRunsQuery({
    includeInactive: showInactive,
  });
  // `data` is set only by a successful response, and RTK Query keeps it while a new argument loads.
  const loaded = runList !== undefined;
  const runs = runList?.runs ?? NO_RUNS;
  const [deleteRun, { isLoading: deleting }] =
    useDeleteContractModelDraftRunMutation();
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // CLE-2811 — a super admin lists every hospital's runs; the Hospital and Created-by filters narrow the loaded rows
  // client-side and are remembered per browser (`runListFilters.ts`). `saved` is what she chose, remembered as-is;
  // `applied` is derived from it and the options on offer. A change writes only the field she changed, so a saved
  // choice that is merely not on offer right now (its fallback is showing) is never overwritten by that fallback.
  const currentUserId = useOptionalUserWithAccount()?.userInfo?.id;
  const [saved, setSaved] = useState<IRunListFilters>(readRunListFilters);
  const saveFilters = (next: IRunListFilters) => {
    setSaved(next);
    writeRunListFilters(next);
  };
  const chooseFilter = (field: keyof IRunListFilters, value: string) =>
    saveFilters({ ...saved, [field]: value });
  // Before the first successful load there are no rows to offer: show the saved choices as they are (disabled) rather
  // than fall back and then jump when the rows arrive.
  const pending = useMemo(
    () => (loaded ? null : pendingRunFilterOptions(saved)),
    [loaded, saved]
  );
  const hospitals = useMemo(
    () => pending?.hospitals ?? hospitalOptions(runs),
    [pending, runs]
  );
  const creators = useMemo(
    () => pending?.creators ?? creatorOptions(runs, currentUserId),
    [pending, runs, currentUserId]
  );
  const applied = useMemo(
    () =>
      loaded ? effectiveRunListFilters(saved, hospitals, creators) : saved,
    [loaded, saved, hospitals, creators]
  );
  const visibleRuns = useMemo(
    () => filterRuns(runs, applied, currentUserId),
    [runs, applied, currentUserId]
  );
  const filtersHideEveryRow = runs.length > 0 && visibleRuns.length === 0;

  const openRun = (id: string) => navigate(`/contract-model-drafts/${id}`);

  // Offered only on a canceled run with no recommendations; the server is the authority (409 + reason).
  const handleDeleteRun = (run: IDraftRunSummary) => {
    confirmDialog({
      header: DELETE_MODEL_HEADER,
      message: DELETE_MODEL_MESSAGE,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: DELETE_MODEL,
      rejectLabel: "Keep it",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteRun(run.id).unwrap();
          toastRef?.current?.show({
            severity: "success",
            summary: MODEL_DELETED,
            life: 4000,
          });
        } catch (e) {
          toastRef?.current?.show({
            severity: "error",
            summary: DELETE_MODEL_FAILED,
            detail: errorDetail(e),
            life: 7000,
          });
        }
      },
    });
  };

  const createdByBody = (r: IDraftRunSummary) => creatorLabel(r, currentUserId);

  const actionsBody = (r: IDraftRunSummary) =>
    canDeleteRun(r) ? (
      <Button
        label={DELETE_MODEL}
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        disabled={deleting}
        onClick={(e) => {
          // The row itself opens the run.
          e.stopPropagation();
          handleDeleteRun(r);
        }}
      />
    ) : null;

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-3">
        <h1 className="m-0">AI Contract Modeling</h1>
        {activeTab === 0 && (
          <div className="flex align-items-center gap-3">
            <label
              htmlFor="show-inactive-runs"
              className="flex align-items-center gap-2 text-700"
            >
              <InputSwitch
                inputId="show-inactive-runs"
                checked={showInactive}
                onChange={(e) => setShowInactive(!!e.value)}
              />
              Show canceled and failed
            </label>
            <Button
              label={NEW_CONTRACT_MODEL}
              icon="pi pi-plus"
              onClick={() => setShowCreate(true)}
            />
          </div>
        )}
      </div>

      <TabView
        activeIndex={activeTab}
        onTabChange={(e) => setActiveTab(e.index)}
      >
        <TabPanel header={CONTRACT_MODELS_TAB}>
          <div className="flex flex-wrap align-items-end gap-3 mb-3">
            <div className="flex flex-column gap-1">
              <label htmlFor="runs-hospital-filter" className="text-700">
                {HOSPITAL_COLUMN}
              </label>
              <Dropdown
                inputId="runs-hospital-filter"
                value={applied.hospital}
                options={hospitals}
                filter={hospitals.length > 8}
                disabled={!loaded}
                onChange={(e) => chooseFilter("hospital", e.value as string)}
                style={{ minWidth: "14rem" }}
              />
            </div>
            <div className="flex flex-column gap-1">
              <label htmlFor="runs-creator-filter" className="text-700">
                {CREATED_BY_COLUMN}
              </label>
              <Dropdown
                inputId="runs-creator-filter"
                value={applied.creator}
                options={creators}
                filter={creators.length > 8}
                disabled={!loaded}
                onChange={(e) => chooseFilter("creator", e.value as string)}
                style={{ minWidth: "14rem" }}
              />
            </div>
          </div>
          {runList?.truncated && (
            <p className="text-600 text-sm mt-0 mb-2">
              {runListTruncated(runList.limit ?? runs.length)}
            </p>
          )}
          <PageDataTable
            value={visibleRuns}
            dataKey="id"
            emptyMessage={
              filtersHideEveryRow ? (
                <span>
                  {NO_MATCHING_CONTRACT_MODELS}{" "}
                  <Button
                    label={CLEAR_FILTERS}
                    className="p-button-link p-0 w-auto"
                    onClick={() => saveFilters(DEFAULT_RUN_LIST_FILTERS)}
                  />
                </span>
              ) : (
                CONTRACT_MODELS_EMPTY
              )
            }
            rowClassName={() => "cursor-pointer"}
            onRowClick={(e) => openRun((e.data as IDraftRunSummary).id)}
          >
            <Column header="Name" body={nameBody} />
            <Column header={HOSPITAL_COLUMN} body={hospitalBody} />
            <Column header="Status" body={statusBody} />
            <Column header="Documents" field="documentCount" />
            <Column header="Recommendations" field="recommendationCount" />
            <Column header={CREATED_BY_COLUMN} body={createdByBody} />
            <Column header="Created" body={createdBody} />
            <Column body={actionsBody} style={{ width: "9rem" }} />
          </PageDataTable>
        </TabPanel>
        <TabPanel header="Standing instructions">
          <StandingInstructionsTab />
        </TabPanel>
      </TabView>

      <NewContractModelDialog
        visible={showCreate}
        onHide={() => setShowCreate(false)}
      />
    </div>
  );
}
